import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Debounced autosave with a single in-flight request.
 *
 * The contract the caller gets is narrow on purpose: hand it a complete payload
 * whenever the draft changes, and it decides when that payload is worth sending.
 * Everything that makes autosave go wrong is handled here rather than in the
 * page — overlapping requests, a response landing after the user has typed
 * again, a no-op save because nothing actually changed, and edits still sitting
 * in the debounce when the component unmounts.
 *
 * Two rules explain most of the implementation:
 *
 *  1. Never more than one request at a time. A save requested while one is in
 *     flight sets a flag instead, and runs once the first settles. Requests are
 *     therefore totally ordered, which matters because the endpoints this backs
 *     replace a whole document rather than patching fields — two overlapping
 *     writes could otherwise land in the wrong order and revert an edit.
 *  2. The baseline is the payload the server last accepted. A draft equal to the
 *     baseline is not sent, so blurring an untouched field, or typing a
 *     character and deleting it, costs nothing.
 */

/** Backoff for retryable failures, jittered so many tabs do not resynchronise. */
const RETRY_DELAYS_MS = [2000, 6000, 15000];

export const AUTOSAVE_IDLE = 'idle';
export const AUTOSAVE_DIRTY = 'dirty';
export const AUTOSAVE_SAVING = 'saving';
/** Edits the caller asked us to withhold, e.g. a half-typed email address. */
export const AUTOSAVE_HELD = 'held';
export const AUTOSAVE_ERROR = 'error';

function withJitter(ms) {
  return ms + Math.round(Math.random() * Math.min(1000, ms * 0.3));
}

/**
 * A transport failure or a server fault is worth repeating; a payload the server
 * has rejected on its merits is not, and 401 is already handled by the api
 * client's refresh-and-replay interceptor.
 */
export function isRetryableError(error) {
  const status = error?.response?.status;
  if (status == null) return true;
  return status === 429 || status >= 500;
}

export default function useAutosave({ save, delay = 900, serialize = JSON.stringify }) {
  const [status, setStatus] = useState(AUTOSAVE_IDLE);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  const saveRef = useRef(save);
  saveRef.current = save;
  const serializeRef = useRef(serialize);
  serializeRef.current = serialize;
  const delayRef = useRef(delay);
  delayRef.current = delay;

  /** Serialised form of the payload the server last accepted. */
  const baselineRef = useRef(null);
  /** `{ value }` while an edit is waiting to be sent, otherwise null. */
  const draftRef = useRef(null);
  const heldRef = useRef(false);

  const timerRef = useRef(null);
  const retryTimerRef = useRef(null);
  const runningRef = useRef(false);
  const queuedRef = useRef(false);
  const attemptRef = useRef(0);
  const mountedRef = useRef(true);
  const runRef = useRef(null);

  const apply = useCallback((fn) => {
    if (mountedRef.current) fn();
  }, []);

  const clearTimers = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const run = useCallback(async () => {
    clearTimers();

    if (runningRef.current) {
      queuedRef.current = true;
      return;
    }

    const pending = draftRef.current;
    if (!pending) return;

    if (heldRef.current) {
      apply(() => setStatus(AUTOSAVE_HELD));
      return;
    }

    const payload = pending.value;
    const key = serializeRef.current(payload);
    if (key === baselineRef.current) {
      draftRef.current = null;
      apply(() => {
        setError(null);
        setStatus(AUTOSAVE_IDLE);
      });
      return;
    }

    runningRef.current = true;
    apply(() => {
      setError(null);
      setStatus(AUTOSAVE_SAVING);
    });

    try {
      await saveRef.current(payload);
      baselineRef.current = key;
      attemptRef.current = 0;

      // Anything typed while the request was open is still unsaved.
      const latest = draftRef.current;
      const stillPending = Boolean(latest) && serializeRef.current(latest.value) !== key;
      if (!stillPending) draftRef.current = null;

      apply(() => {
        setError(null);
        setSavedAt(Date.now());
        setStatus(stillPending ? AUTOSAVE_DIRTY : AUTOSAVE_IDLE);
      });
      if (stillPending) queuedRef.current = true;
    } catch (err) {
      apply(() => {
        setError(err);
        setStatus(AUTOSAVE_ERROR);
      });
      if (isRetryableError(err) && attemptRef.current < RETRY_DELAYS_MS.length) {
        const wait = withJitter(RETRY_DELAYS_MS[attemptRef.current]);
        attemptRef.current += 1;
        retryTimerRef.current = setTimeout(() => {
          retryTimerRef.current = null;
          runRef.current?.();
        }, wait);
      }
    } finally {
      runningRef.current = false;
      if (queuedRef.current) {
        queuedRef.current = false;
        runRef.current?.();
      }
    }
  }, [apply, clearTimers]);
  runRef.current = run;

  /**
   * Declares what the server already holds, without scheduling anything. Called
   * once the initial fetch resolves so the first edit is measured against it.
   */
  const setBaseline = useCallback(
    (value) => {
      clearTimers();
      baselineRef.current = serializeRef.current(value);
      draftRef.current = null;
      heldRef.current = false;
      attemptRef.current = 0;
      queuedRef.current = false;
      apply(() => {
        setError(null);
        setStatus(AUTOSAVE_IDLE);
      });
    },
    [apply, clearTimers]
  );

  /**
   * Records a new draft and restarts the debounce. `hold` keeps the payload out
   * of the request while the caller considers it invalid; the status only admits
   * to holding once the debounce has elapsed, so a field is never marked wrong
   * mid-keystroke.
   *
   * Returns `'clean'` when the draft has come back to what the server already
   * holds, so callers can discard any copy they were keeping against a crash.
   */
  const change = useCallback(
    (value, { hold = false } = {}) => {
      clearTimers();
      attemptRef.current = 0;
      heldRef.current = hold;

      const key = serializeRef.current(value);
      if (!hold && key === baselineRef.current) {
        draftRef.current = null;
        apply(() => {
          setError(null);
          setStatus(runningRef.current ? AUTOSAVE_SAVING : AUTOSAVE_IDLE);
        });
        return 'clean';
      }

      draftRef.current = { value };
      apply(() => {
        setError(null);
        setStatus(runningRef.current ? AUTOSAVE_SAVING : AUTOSAVE_DIRTY);
      });
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        runRef.current?.();
      }, delayRef.current);
      return hold ? 'held' : 'pending';
    },
    [apply, clearTimers]
  );

  /** Commits now rather than waiting out the debounce — used on blur. */
  const flush = useCallback(() => run(), [run]);

  const retry = useCallback(() => {
    attemptRef.current = 0;
    return run();
  }, [run]);

  /**
   * True whenever the server does not yet hold what the user sees. Drives the
   * unload guard and lets callers wait before navigating away.
   */
  const hasUnsavedChanges =
    status === AUTOSAVE_DIRTY ||
    status === AUTOSAVE_HELD ||
    status === AUTOSAVE_SAVING ||
    status === AUTOSAVE_ERROR;

  // A failure that was only ever about connectivity should clear itself the
  // moment connectivity returns, rather than waiting out the backoff.
  useEffect(() => {
    if (status !== AUTOSAVE_ERROR) return undefined;
    const onOnline = () => runRef.current?.();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [status]);

  // Backgrounding a tab on mobile often means it is discarded without ever
  // firing unload, so the pending edit is committed on the way out.
  useEffect(() => {
    const commit = () => {
      if (document.visibilityState === 'hidden') runRef.current?.();
    };
    document.addEventListener('visibilitychange', commit);
    window.addEventListener('pagehide', commit);
    return () => {
      document.removeEventListener('visibilitychange', commit);
      window.removeEventListener('pagehide', commit);
    };
  }, []);

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    const warn = (event) => {
      event.preventDefault();
      // Chrome still requires the legacy assignment to raise the prompt.
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsavedChanges]);

  useEffect(
    () => () => {
      mountedRef.current = false;
      clearTimers();

      // Leaving the page must not discard an edit that was still in the
      // debounce. A request already in flight will pick the newer draft up in
      // its own `finally`, which also keeps the two writes ordered.
      const pending = draftRef.current;
      if (!pending || heldRef.current) return;
      if (runningRef.current) {
        queuedRef.current = true;
        return;
      }
      if (serializeRef.current(pending.value) === baselineRef.current) return;
      Promise.resolve()
        .then(() => saveRef.current(pending.value))
        .catch(() => {});
    },
    [clearTimers]
  );

  return {
    status,
    error,
    savedAt,
    hasUnsavedChanges,
    setBaseline,
    change,
    flush,
    retry,
  };
}
